import ContentIllustration from '../../../../components/ui/ContentIllustration';
import { Lock, Unlock, Gift, Mail, Eye, Check, Copy } from 'lucide-react';
import { scoreColor } from './helpers';

// Tables du suivi par codes (catégorie / mono quiz). Présentation pure :
// le parent garde le chargement des données et l'action seal/unseal (onToggleSeal).

export function CategoryCodesTracking({ loading, codes, copiedCode, copyCode, onDetail, onToggleSeal }) {
    if (loading) return <div className="loading">Chargement...</div>;
    if (codes.length === 0) return (
        <div className="sl-empty-state">
            <span className="sl-empty-icon"><ContentIllustration name="layers" size={64} /></span>
            <p>Aucun code cat&eacute;gorie g&eacute;n&eacute;r&eacute;. Rendez-vous dans Partager &gt; Acheter &gt; Cat&eacute;gorie.</p>
        </div>
    );
    const enCours = codes.filter(c => c.status === 'IN_PROGRESS').length;
    const dispo = codes.filter(c => c.status === 'AVAILABLE').length;
    return (
        <>
            <div className="codes-summary">
                <div className="codes-stat"><span className="codes-stat__num">{codes.length}</span><span className="codes-stat__lbl">codes g&eacute;n&eacute;r&eacute;s</span></div>
                <div className="codes-stat"><span className="codes-stat__num" style={{ color: '#f59e0b' }}>{enCours}</span><span className="codes-stat__lbl">en cours</span></div>
                <div className="codes-stat"><span className="codes-stat__num" style={{ color: 'var(--text-tertiary, #94a3b8)' }}>{dispo}</span><span className="codes-stat__lbl">disponibles</span></div>
            </div>
            <div className="codes-table codes-table--cat">
                <div className="codes-table__head">
                    <div>Code</div><div>Cat&eacute;gorie</div><div>Groupe</div><div>Mode</div><div>Participant</div><div>Statut</div><div>Progression</div><div>Activit&eacute;</div><div style={{ textAlign: 'center' }}>D&eacute;tail</div>
                </div>
                {codes.map(c => {
                    const pct = c.total_points > 0 ? Math.round((c.total_score / c.total_points) * 100) : null;
                    const lastActivity = c.last_child_finished_at || c.started_at;
                    const inProgress = c.status === 'IN_PROGRESS';
                    const finished = c.status === 'FINISHED';
                    const hasDetail = !!(c.participant_name || c.nb_finished > 0 || c.nb_total > 0);
                    return (
                        <div key={c.id} className={`codes-table__row ${inProgress ? 'is-active' : ''} ${hasDetail ? 'is-clickable' : ''}`}
                             onClick={hasDetail ? () => onDetail(c.id) : undefined}
                             title={hasDetail ? 'Voir le détail' : undefined}>
                            <div className="codes-code">
                                <span>{c.code}</span>
                                <button type="button" className="codes-copy" onClick={(e) => { e.stopPropagation(); copyCode(c.code); }} title="Copier le code">
                                    {copiedCode === c.code ? <Check size={13} /> : <Copy size={13} />}
                                </button>
                            </div>
                            <div className="codes-sujet">{c.category_label}</div>
                            <div>{c.label ? <span className="codes-group">{c.label}</span> : <span className="codes-dash">&mdash;</span>}</div>
                            <div className="codes-mode">
                                {c.mode === 'sealed' ? <><Lock size={11} style={{ verticalAlign: 'middle' }} /> Scellé</> : c.mode === 'apprentissage' ? 'Apprent.' : 'Classique'}
                                {!!Number(c.is_gift) && <Gift size={11} style={{ marginLeft: 4, color: '#ec4899', verticalAlign: 'middle' }} />}
                            </div>
                            <div className="codes-part">
                                {c.participant_name || (c.email_invitation
                                    ? <span style={{ color: 'var(--text-muted)' }} title={`Invitation envoyée à ${c.email_invitation}`}><Mail size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />{c.email_invitation.split('@')[0]}</span>
                                    : <span className="codes-dash">&mdash;</span>)}
                            </div>
                            <div className="codes-statut">
                                <span className={`codes-badge codes-badge--${c.status === 'AVAILABLE' ? 'dispo' : inProgress ? 'progress' : finished ? 'done' : 'dispo'}`}>
                                    {c.status === 'AVAILABLE' ? 'Dispo' : inProgress ? 'En cours' : finished ? 'Terminé' : c.status}
                                </span>
                            </div>
                            <div className="codes-prog">
                                {c.nb_total > 0
                                    ? <span><b>{c.nb_finished}/{c.nb_total}</b>{pct !== null && <span style={{ color: scoreColor(pct), marginLeft: 4 }}>({pct}%)</span>}</span>
                                    : <span className="codes-dash">&mdash;</span>}
                            </div>
                            <div className="codes-date">
                                {lastActivity ? new Date(lastActivity).toLocaleDateString('fr-FR') : <span className="codes-dash">&mdash;</span>}
                            </div>
                            <div className="codes-detail">
                                {c.mode === 'sealed' && c.nb_finished > 0 && (
                                    <button type="button" className="codes-seal"
                                        onClick={(e) => { e.stopPropagation(); onToggleSeal(c); }}
                                        title={c.review_released_at ? 'Resceller les résultats' : 'Desceller les résultats'}
                                        style={{ color: c.review_released_at ? '#10b981' : '#f59e0b' }}>
                                        {c.review_released_at ? <Unlock size={13} /> : <Lock size={13} />}
                                    </button>
                                )}
                                {(c.participant_name || c.nb_finished > 0 || c.nb_total > 0) && (
                                    <button type="button" className="codes-copy" onClick={(e) => { e.stopPropagation(); onDetail(c.id); }} title="Voir le détail">
                                        <Eye size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="codes-foot">Le code en cours est mis en &eacute;vidence · clique <Copy size={11} style={{ verticalAlign: 'middle' }} /> pour copier un code</div>
        </>
    );
}

export function QuizCodesTracking({ loading, codes, copiedCode, copyCode, onReview, onToggleSeal }) {
    if (loading) return <div className="loading">Chargement...</div>;
    if (codes.length === 0) return (
        <div className="sl-empty-state">
            <span className="sl-empty-icon"><ContentIllustration name="key-round" size={64} /></span>
            <p>Aucun code quiz g&eacute;n&eacute;r&eacute;. Rendez-vous dans Partager &gt; Acheter &gt; Mono quiz.</p>
        </div>
    );
    const enCours = codes.filter(c => c.status === 'IN_PROGRESS').length;
    const dispo = codes.filter(c => c.status === 'AVAILABLE').length;
    return (
        <>
            <div className="codes-summary">
                <div className="codes-stat"><span className="codes-stat__num">{codes.length}</span><span className="codes-stat__lbl">codes g&eacute;n&eacute;r&eacute;s</span></div>
                <div className="codes-stat"><span className="codes-stat__num" style={{ color: '#f59e0b' }}>{enCours}</span><span className="codes-stat__lbl">en cours</span></div>
                <div className="codes-stat"><span className="codes-stat__num" style={{ color: 'var(--text-tertiary, #94a3b8)' }}>{dispo}</span><span className="codes-stat__lbl">disponibles</span></div>
            </div>
            <div className="codes-table codes-table--quiz">
                <div className="codes-table__head">
                    <div>Code</div><div>Sujet</div><div>Groupe</div><div>Mode</div><div>Participant</div><div>Statut</div><div style={{ textAlign: 'right' }}>Score</div>
                </div>
                {codes.map(c => {
                    const pct = c.status === 'FINISHED' && c.total > 0 ? Math.round((c.score / c.total) * 100) : null;
                    const inProgress = c.status === 'IN_PROGRESS';
                    const finished = c.status === 'FINISHED';
                    const modeLabel = c.mode === 'apprentissage' ? 'Apprent.' : c.mode === 'sealed' ? 'Scellé' : 'Classique';
                    return (
                        <div key={c.id} className={`codes-table__row ${inProgress ? 'is-active' : ''} ${finished ? 'is-clickable' : ''}`}
                             onClick={finished ? () => onReview(c.id) : undefined}
                             title={finished ? 'Voir la session complète' : undefined}>
                            <div className="codes-code">
                                <span>{c.code}</span>
                                <button type="button" className="codes-copy" onClick={(e) => { e.stopPropagation(); copyCode(c.code); }} title="Copier le code">
                                    {copiedCode === c.code ? <Check size={13} /> : <Copy size={13} />}
                                </button>
                            </div>
                            <div className="codes-sujet">{c.sous_sujet_label}</div>
                            <div>{c.label ? <span className="codes-group">{c.label}</span> : <span className="codes-dash">&mdash;</span>}</div>
                            <div className="codes-mode">{c.mode === 'sealed' && <Lock size={11} style={{ verticalAlign: 'middle' }} />} {modeLabel}</div>
                            <div className="codes-part">{c.participant_name || <span className="codes-dash">&mdash;</span>}</div>
                            <div className="codes-statut">
                                <span className={`codes-badge codes-badge--${c.status === 'AVAILABLE' ? 'dispo' : inProgress ? 'progress' : finished ? 'done' : 'dispo'}`}>
                                    {c.status === 'AVAILABLE' ? 'Dispo' : inProgress ? 'En cours' : finished ? 'Terminé' : c.status}
                                </span>
                                {finished && c.mode === 'sealed' && (
                                    <button type="button" className="codes-seal"
                                        onClick={(e) => { e.stopPropagation(); onToggleSeal(c); }}
                                        title={c.review_released_at ? 'Resceller les résultats' : 'Desceller les résultats'}
                                        style={{ color: c.review_released_at ? '#10b981' : '#f59e0b' }}>
                                        {c.review_released_at ? <Unlock size={13} /> : <Lock size={13} />}
                                    </button>
                                )}
                            </div>
                            <div className="codes-score">
                                {pct != null
                                    ? <span style={{ color: scoreColor(pct), fontWeight: 700 }}>{c.score}/{c.total}</span>
                                    : inProgress && c.started_at
                                        ? <span className="codes-date">{new Date(c.started_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                                        : <span className="codes-dash">&mdash;</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="codes-foot">Le code en cours est mis en &eacute;vidence · clique <Copy size={11} style={{ verticalAlign: 'middle' }} /> pour copier un code</div>
        </>
    );
}
